import { useEffect, useState } from "react";

// Read-only print view. Shares the localStorage key and shape with
// HolyWeek.tsx; intentionally does NOT import the editable view so
// this page has no chrome (no DashboardHeader, no buttons, no tabs).
// Open in a new tab; Cmd+P from there prints clean letter-paper
// pages Colleen can three-hole-punch.

interface Ministry {
  name: string;
  slots: number;
}

interface Service {
  id: string;
  day: string;
  dayLabel: string;
  service: string;
  times: string;
  ministries: Ministry[];
}

const STORAGE_KEY = "sacristy.holyWeek.v1";

// Fallback mirrors HolyWeek.tsx's INITIAL_SERVICES. Duplicated rather
// than imported so the print bundle stays standalone and the editable
// view can evolve without breaking this layout.
const FALLBACK_SERVICES: Service[] = [
  {
    id: "service-thursday",
    day: "Thursday, March 25",
    dayLabel: "Thursday, March 25",
    service: "Mass of the Lord's Supper",
    times: "7:00 PM",
    ministries: [
      { name: "Sacristan", slots: 1 },
      { name: "EMHC", slots: 8 },
      { name: "Lector", slots: 3 },
      { name: "Usher", slots: 4 },
      { name: "Hospitality", slots: 2 },
    ],
  },
  {
    id: "service-friday",
    day: "Friday, March 26",
    dayLabel: "Friday, March 26",
    service: "Celebration of the Lord's Passion",
    times: "12:00 PM and 7:00 PM",
    ministries: [
      { name: "Sacristan", slots: 1 },
      { name: "Lector", slots: 3 },
      { name: "Usher", slots: 4 },
    ],
  },
  {
    id: "service-saturday",
    day: "Saturday, March 27",
    dayLabel: "Saturday, March 27",
    service: "Easter Vigil",
    times: "8:30 PM",
    ministries: [
      { name: "Sacristan", slots: 2 },
      { name: "EMHC", slots: 10 },
      { name: "Lector", slots: 7 },
      { name: "Usher", slots: 4 },
      { name: "Hospitality", slots: 2 },
    ],
  },
];

function loadServices(): Service[] {
  if (typeof window === "undefined") return FALLBACK_SERVICES;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return FALLBACK_SERVICES;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return FALLBACK_SERVICES;
    return parsed as Service[];
  } catch {
    return FALLBACK_SERVICES;
  }
}

export default function HolyWeekPrint() {
  const [services] = useState<Service[]>(() => loadServices());

  // Add a body class for the print view so the global stylesheet can
  // strip the page padding / app chrome only on this route, and an
  // explicit document title so the printed header / save-as dialog
  // both show something meaningful.
  useEffect(() => {
    document.body.classList.add("is-print-view");
    const prevTitle = document.title;
    document.title = "Holy Week 2027 — Our Lady of Grace";
    return () => {
      document.body.classList.remove("is-print-view");
      document.title = prevTitle;
    };
  }, []);

  return (
    <main className="hw-print">
      <header className="hw-print__header">
        <h1 className="hw-print__parish">Our Lady of Grace</h1>
        <p className="hw-print__title">Holy Week 2027</p>
      </header>

      {services.map((s) => (
        <section key={s.id} className="hw-print__service">
          <div className="hw-print__service-head">
            <div className="hw-print__day">{s.dayLabel || s.day}</div>
            <div className="hw-print__time">{s.times}</div>
          </div>
          <p className="hw-print__service-name">{s.service}</p>
          <div className="hw-print__ministries">
            {s.ministries.map((m, mi) => (
              <div key={mi} className="hw-print__ministry">
                <h3 className="hw-print__ministry-name">{m.name}</h3>
                <ol className="hw-print__slots">
                  {Array.from({ length: m.slots }).map((_, i) => (
                    <li key={i} className="hw-print__slot">
                      {/* When volunteer assignment is wired, the
                          assigned name renders here in regular
                          weight; an empty span yields a signature
                          line for handwriting. */}
                      <span className="hw-print__slot-line" />
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>
      ))}

      <footer className="hw-print__footer">Sacristy AI</footer>
    </main>
  );
}
